import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "../i18n";
import { LangSwitcher } from "../ui/LangSwitcher";
import { submitWaitlist } from "../lib/analytics";

export function Waitlist() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ first: "", last: "", email: "", phone: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = form.first.trim() && form.last.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setErr(null);
    const res = await submitWaitlist({ ...form, lang });
    setBusy(false);
    if (res.ok) setSent(true);
    else setErr(res.error ?? "Could not submit — please retry.");
  };

  const field =
    "w-full px-5 py-4 rounded-xl border border-[var(--color-ink)]/15 bg-white/60 focus:outline-none focus:border-[var(--color-moss)] transition-colors";

  return (
    <div className="min-h-screen bg-[var(--color-cream)] flex flex-col">
      <header className="px-8 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <span className="w-6 h-6 rounded-full bg-[var(--color-forest-deep)]" />
          <span className="font-logo text-sm text-[var(--color-forest-deep)]">ALVION</span>
        </Link>
        <div className="flex items-center gap-3">
          <LangSwitcher />
          <Link to="/" className="text-sm text-[var(--color-muted)] hover:text-[var(--color-ink)]">
            {t("nav.close")}
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center"
              >
                <h1 className="font-serif text-5xl text-[var(--color-ink)]">{t("wl.done.title")}</h1>
                <p className="mt-4 text-[var(--color-muted)]">{t("wl.done.sub")}</p>
                <Link to="/" className="pill-btn mt-10 inline-flex">
                  {t("survey.home")}
                </Link>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-5"
              >
                <div className="text-center mb-8">
                  <h1 className="font-serif text-4xl md:text-5xl text-[var(--color-ink)]">{t("wl.title")}</h1>
                  <p className="mt-3 text-[var(--color-muted)]">{t("wl.sub")}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <input
                    className={field}
                    placeholder={t("wl.first")}
                    value={form.first}
                    onChange={(e) => setForm({ ...form, first: e.target.value })}
                    required
                  />
                  <input
                    className={field}
                    placeholder={t("wl.last")}
                    value={form.last}
                    onChange={(e) => setForm({ ...form, last: e.target.value })}
                    required
                  />
                </div>

                <input
                  type="email"
                  className={field}
                  placeholder={t("wl.email")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />

                <input
                  type="tel"
                  className={field}
                  placeholder={t("wl.phone")}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />

                {err && <p className="text-sm text-red-700/80">{err}</p>}

                <button
                  type="submit"
                  disabled={!valid || busy}
                  className="pill-btn w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "…" : t("wl.submit")}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
